import { useState } from 'react';
import { createEntrevista } from '../api/entrevistaApi';

export function useEntrevistaForm({ idPaciente, navigate }) {
  const pacienteId = idPaciente?.toString();

  // ── Step ────────────────────────────────────────────────────────────
  const [step, setStep] = useState(0);
  const [stepErrors, setStepErrors] = useState({});
  const [mostrarDatosCompletos, setMostrarDatosCompletos] = useState(false);

  // ── Identificación ───────────────────────────────────────────────────
  const [tipoEntrevista, setTipoEntrevista] = useState('');

  // ── Hábitos ──────────────────────────────────────────────────────────
  const [fumador, setFumador] = useState('');
  const [tiempoFumador, setTiempoFumador] = useState('');
  const [aniosDeshabilitacion, setAniosDeshabilitacion] = useState('');
  const [alcohol, setAlcohol] = useState('');
  const [tiempoAlcohol, setTiempoAlcohol] = useState('');
  const [aniosDeshabilitacionAlcohol, setAniosDeshabilitacionAlcohol] = useState('');

  // ── Movilidad ─────────────────────────────────────────────────────────
  const [caidas12Meses, setCaidas12Meses] = useState(false);
  const [numCaidas, setNumCaidas] = useState('');
  const [ayudaLocomocion, setAyudaLocomocion] = useState(false);
  const [protesisArticulares, setProtesisArticulares] = useState(false);
  const [protesis, setProtesis] = useState({
    rodilla: false, cadera: false, hombro: false, manoDedos: false, otros: '',
  });
  const [vertigos, setVertigos] = useState(false);
  const [mareosAndar, setMareosAndar] = useState(false);

  // ── Sistemas clínicos ─────────────────────────────────────────────────
  const [cardiovascular, setCardiovascular] = useState(false);
  const [cardioOptions, setCardioOptions] = useState({
    hipertension: false, insuficienciaCardiaca: false, accCerebrovascular: false,
    anginaPecho: false, infarto: false, trombosis: false, otros: '',
  });
  const [respiratorio, setRespiratorio] = useState(false);
  const [respOptions, setRespOptions] = useState({ asma: false, epoc: false, otros: '' });
  const [genitoUrinario, setGenitoUrinario] = useState(false);
  const [genitoOptions, setGenitoOptions] = useState({
    infeccionUrinaria: false, calculosRenales: false, insuficienciaRenal: false,
    incontinenciaUrinaria: false, prostata: false, otros: '',
  });
  const [endocrino, setEndocrino] = useState(false);
  const [endocrinoOptions, setEndocrinoOptions] = useState({
    diabetes: false, hipotiroidismo: false, hipercolesterolemia: false, osteoporosis: false, otros: '',
  });
  const [digestivo, setDigestivo] = useState(false);
  const [digestOptions, setDigestOptions] = useState({
    herniaHiato: false, reflujo: false, pancreatitis: false,
    enfermedadHepatica: false, diverticulitis: false, otros: '',
  });
  const [musculoEsqueletico, setMusculoEsqueletico] = useState(false);
  const [musculoOptions, setMusculoOptions] = useState({
    pieTobillo: false, rodilla: false, cadera: false, lumbar: false,
    dorsal: false, cervical: false, cabeza: false, hombro: false, codo: false, manoMunieca: false,
  });
  const [nervioso, setNervioso] = useState(false);
  const [nervOptions, setNervOptions] = useState({
    depresion: false, ansiedad: false, esclerosisMultiple: false, parkinson: false,
    alzheimer: false, deterioroCognitivo: false, neuropatiaPeriferica: false, otros: '',
  });
  const [sentidos, setSentidos] = useState(false);
  const [sentidosOptions, setSentidosOptions] = useState({
    hipoacusia: false, tinitus: false, perdidaVision: false,
  });

  // ── Medicación y medidas ──────────────────────────────────────────────
  const [medicacion, setMedicacion] = useState(false);
  const [medOptions, setMedOptions] = useState({
    antihipertensivos: false, antidiabeticos: false, estatinas: false,
    anticoagulantes: false, tiroides: false, diuretico: false, analgesicos: false,
    antidepresivos: false, ansioliticos: false, antiosteoporoticos: false,
    inhalador: false, protectorEstomago: false, otros: '',
  });
  const [numAnalgesicos, setNumAnalgesicos] = useState('');
  const [tensionPMM, setTensionPMM] = useState('');
  const [tensionPAS, setTensionPAS] = useState('');
  const [tensionPAD, setTensionPAD] = useState('');
  const [datosInteres, setDatosInteres] = useState('');

  // ── Actividades y cierre ──────────────────────────────────────────────
  const [actividades, setActividades] = useState({
    Revitalización: false, Equilibrio: false, 'Suelo pélvico': false,
    Ventilación: false, TO: false, VENREDI: false,
  });
  const [caidasFinales, setCaidasFinales] = useState('');
  const [numPastillas, setNumPastillas] = useState('');
  const [numAnalgesicosFinal, setNumAnalgesicosFinal] = useState('');
  const [cambioMedicacionFinal, setCambioMedicacionFinal] = useState(false);
  const [consultaMedico, setConsultaMedico] = useState(false);
  const [ingresosHospital, setIngresosHospital] = useState(false);
  const [datosInteresFinal, setDatosInteresFinal] = useState('');

  // ── Feedback ──────────────────────────────────────────────────────────
  const [openGood, setOpenGood] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ── Navigation ────────────────────────────────────────────────────────
  // In "final mode" (Final interview, not yet expanded): 2 steps only.
  // In full mode: 5 steps.
  const isFinalMode = tipoEntrevista === 'Final' && !mostrarDatosCompletos;
  const TOTAL_STEPS = isFinalMode ? 2 : 5;

  const validateStep = (s) => {
    if (s === 0 && !tipoEntrevista) {
      setStepErrors({ tipoEntrevista: 'Selecciona el tipo de entrevista' });
      return false;
    }
    setStepErrors({});
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) setStep((p) => Math.min(p + 1, TOTAL_STEPS - 1));
  };

  const prevStep = () => {
    setStepErrors({});
    setStep((p) => Math.max(p - 1, 0));
  };

  // Expand to full form from final mode (keeps step=1 → goes to Hábitos)
  const expandarHistorialCompleto = () => {
    setMostrarDatosCompletos(true);
    setStep(1);
  };

  // ── Save ──────────────────────────────────────────────────────────────
  const guardarDatos = () => {
    if (isSaving) return;
    setIsSaving(true);

    const numFumador = fumador === 'No' ? 1 : fumador === 'Sí' ? 2 : 3;
    const numAlcohol = alcohol === 'No' ? 1 : alcohol === 'Sí' ? 2 : 3;
    const actividadesSeleccionadas = Object.keys(actividades).filter((k) => actividades[k]);

    createEntrevista({
      pacienteId,
      payload: {
        tipoEntrevista,
        fumador: numFumador,
        tiempoFumador: parseInt(tiempoFumador, 10) || null,
        aniosDeshabilitacion: parseInt(aniosDeshabilitacion, 10) || null,
        alcohol: numAlcohol,
        tiempoAlcohol: parseInt(tiempoAlcohol, 10) || null,
        aniosDeshabilitacionAlcohol: parseInt(aniosDeshabilitacionAlcohol, 10) || null,
        caidas12Meses,
        numCaidas: parseInt(numCaidas, 10) || null,
        ayudaLocomocion,
        protesisArticulares,
        protesisRodilla: protesis.rodilla,
        protesisCadera: protesis.cadera,
        protesisHombro: protesis.hombro,
        protesisManoDedos: protesis.manoDedos,
        protesisOtros: protesis.otros,
        vertigos,
        mareosAndar,
        cardiovascular,
        hipertension: cardioOptions.hipertension,
        insuficienciaCardiaca: cardioOptions.insuficienciaCardiaca,
        accCerebrovascular: cardioOptions.accCerebrovascular,
        anginaPecho: cardioOptions.anginaPecho,
        infarto: cardioOptions.infarto,
        trombosis: cardioOptions.trombosis,
        otrosCardiovascular: cardioOptions.otros,
        respiratorio,
        asma: respOptions.asma,
        epoc: respOptions.epoc,
        otrosRespiratorio: respOptions.otros,
        genitoUrinario,
        infeccionUrinaria: genitoOptions.infeccionUrinaria,
        calculosRenales: genitoOptions.calculosRenales,
        insuficienciaRenal: genitoOptions.insuficienciaRenal,
        incontinenciaUrinaria: genitoOptions.incontinenciaUrinaria,
        prostata: genitoOptions.prostata,
        otrosGenitoUrinario: genitoOptions.otros,
        endocrino,
        diabetes: endocrinoOptions.diabetes,
        hipotiroidismo: endocrinoOptions.hipotiroidismo,
        hipercolesterolemia: endocrinoOptions.hipercolesterolemia,
        osteoporosis: endocrinoOptions.osteoporosis,
        otrosEndocrino: endocrinoOptions.otros,
        digestivo,
        herniaHiato: digestOptions.herniaHiato,
        reflujo: digestOptions.reflujo,
        pancreatitis: digestOptions.pancreatitis,
        enfermedadHepatica: digestOptions.enfermedadHepatica,
        diverticulitis: digestOptions.diverticulitis,
        otrosDigestivo: digestOptions.otros,
        musculoEsqueletico,
        pieTobillo: musculoOptions.pieTobillo,
        rodilla: musculoOptions.rodilla,
        cadera: musculoOptions.cadera,
        lumbar: musculoOptions.lumbar,
        dorsal: musculoOptions.dorsal,
        cervical: musculoOptions.cervical,
        cabeza: musculoOptions.cabeza,
        hombro: musculoOptions.hombro,
        codo: musculoOptions.codo,
        mano_munieca: musculoOptions.manoMunieca,
        nervioso,
        depresion: nervOptions.depresion,
        ansiedad: nervOptions.ansiedad,
        esclerosisMultiple: nervOptions.esclerosisMultiple,
        parkinson: nervOptions.parkinson,
        alzheimer: nervOptions.alzheimer,
        deterioroCognitivo: nervOptions.deterioroCognitivo,
        neuropatiaPeriferica: nervOptions.neuropatiaPeriferica,
        otrosNervioso: nervOptions.otros,
        sentidos,
        hipoacusia: sentidosOptions.hipoacusia,
        tinitus: sentidosOptions.tinitus,
        perdidaVision: sentidosOptions.perdidaVision,
        medicacion,
        antihipertensivos: medOptions.antihipertensivos,
        antidiabeticos: medOptions.antidiabeticos,
        estatinas: medOptions.estatinas,
        anticoagulantes: medOptions.anticoagulantes,
        tiroides: medOptions.tiroides,
        diuretico: medOptions.diuretico,
        analgesicos: medOptions.analgesicos,
        antidepresivos: medOptions.antidepresivos,
        ansioliticos: medOptions.ansioliticos,
        antiosteoporoticos: medOptions.antiosteoporoticos,
        inhalador: medOptions.inhalador,
        protectorEstomago: medOptions.protectorEstomago,
        otrosMedicacion: medOptions.otros,
        datosInteres,
        numAnalgesicos: parseInt(numAnalgesicos) || 0,
        tensionPMM: parseFloat(tensionPMM) || 0,
        tensionPAS: parseInt(tensionPAS, 10) || 0,
        tensionPAD: parseInt(tensionPAD, 10) || 0,
        actividades: actividadesSeleccionadas.join(', '),
        venredi: actividades.VENREDI,
        terapiaOcupacional: actividades.TO,
        caidasFinales: parseInt(caidasFinales) || 0,
        numPastillas: parseInt(numPastillas) || 0,
        numAnalgesicosFinal: parseInt(numAnalgesicosFinal) || 0,
        cambioMedicacionFinal,
        consultaMedico,
        ingresosHospital,
        datosInteresFinal,
      },
    })
      .then(() => {
        setOpenGood(true);
        setTimeout(() => navigate('/home'), 2000);
      })
      .catch((error) => {
        setOpenError(true);
        console.error('Error al guardar entrevista:', error);
        setIsSaving(false);
      });
  };

  return {
    // navigation
    step, nextStep, prevStep, TOTAL_STEPS, stepErrors,
    isFinalMode, mostrarDatosCompletos, expandarHistorialCompleto,
    // identificación
    tipoEntrevista, setTipoEntrevista,
    // hábitos
    fumador, setFumador,
    tiempoFumador, setTiempoFumador,
    aniosDeshabilitacion, setAniosDeshabilitacion,
    alcohol, setAlcohol,
    tiempoAlcohol, setTiempoAlcohol,
    aniosDeshabilitacionAlcohol, setAniosDeshabilitacionAlcohol,
    // movilidad
    caidas12Meses, setCaidas12Meses,
    numCaidas, setNumCaidas,
    ayudaLocomocion, setAyudaLocomocion,
    protesisArticulares, setProtesisArticulares,
    protesis, setProtesis,
    vertigos, setVertigos,
    mareosAndar, setMareosAndar,
    // sistemas
    cardiovascular, setCardiovascular, cardioOptions, setCardioOptions,
    respiratorio, setRespiratorio, respOptions, setRespOptions,
    genitoUrinario, setGenitoUrinario, genitoOptions, setGenitoOptions,
    endocrino, setEndocrino, endocrinoOptions, setEndocrinoOptions,
    digestivo, setDigestivo, digestOptions, setDigestOptions,
    musculoEsqueletico, setMusculoEsqueletico, musculoOptions, setMusculoOptions,
    nervioso, setNervioso, nervOptions, setNervOptions,
    sentidos, setSentidos, sentidosOptions, setSentidosOptions,
    // medicación y medidas
    medicacion, setMedicacion, medOptions, setMedOptions,
    numAnalgesicos, setNumAnalgesicos,
    tensionPMM, setTensionPMM,
    tensionPAS, setTensionPAS,
    tensionPAD, setTensionPAD,
    datosInteres, setDatosInteres,
    // actividades y cierre
    actividades, setActividades,
    caidasFinales, setCaidasFinales,
    numPastillas, setNumPastillas,
    numAnalgesicosFinal, setNumAnalgesicosFinal,
    cambioMedicacionFinal, setCambioMedicacionFinal,
    consultaMedico, setConsultaMedico,
    ingresosHospital, setIngresosHospital,
    datosInteresFinal, setDatosInteresFinal,
    // feedback
    openGood, setOpenGood,
    openError, setOpenError,
    isSaving,
    guardarDatos,
    pacienteId,
  };
}
